export const COMPARE_MAX_ITEMS = 2;
export const COMPARE_MIN_ITEMS = 1;

export const PHONE_CATEGORY_TAG = 'singtel:product-categories/phones';
export const PHONE_PLANS_GROUP = 'phone-plans';
export const OTHER_BRAND_LABEL = 'Others';

/** Group headers shown in the compare table's plan dropdown. */
export const PHONE_PLANS_HEADER_LABEL = 'Phone Plans';
export const SIM_ONLY_PLANS_HEADER_LABEL = 'SIM Only Plans';

/** Plan dropdown option representing an outright device purchase, with no subscription plan. */
export const DEVICE_ONLY_PLAN_VALUE = 'device-only';
export const DEVICE_ONLY_PLAN_LABEL = 'Device Only';
/** RRP-priced SKU variant plan ID — "Device Only" is only offered when a device has this variant. */
export const DEVICE_ONLY_RRP_PLAN_ID = '8619502_8631722';

/** Curated subset & display order of AEM spec groups surfaced on the compare table; must match AEM's exact `groupTitle` text. */
export const COMPARE_SCHEMA_GROUP_TITLES = [
  'Power and Battery',
  'Camera & Video',
  'Chip and RAM Storage'
];

export type CompareSpec = Record<string, string>;

export const COMPARE_SPEC_BRANDS = {
  APPLE: 'APPLE',
  SAMSUNG: 'SAMSUNG',
  DEFAULT: 'DEFAULT'
} as const;

/** Placeholder specs until AEM exposes device attributes on the Phone node. */
export const COMPARE_SPEC_MOCK: Record<string, CompareSpec> = {
  [COMPARE_SPEC_BRANDS.APPLE]: {
    display:
      '6.7-inch Super Retina XDR OLED, 120Hz ProMotion, HDR10, 2000 nits peak brightness',
    battery:
      'Built-in rechargeable lithium-ion battery, up to 29 hours video playback',
    camera:
      '48MP Main (24mm, f/1.78), 48MP Ultra Wide (13mm), 12MP 5x Telephoto (120mm), 4K Dolby Vision HDR at 60 fps',
    chip:
      'A18 Pro chip with 6-core CPU, 6-core GPU, 16-core Neural Engine, 8GB RAM',
    os: 'iOS 18 with Apple Intelligence',
    connectivity:
      '5G (sub-6 GHz and mmWave), Wi-Fi 7 (802.11be), Bluetooth 5.3, Ultra Wideband (UWB), NFC',
    charging:
      'MagSafe wireless charging up to 25W, Qi2 up to 15W, fast-charge to 50% in 30 mins',
    dimensions: '163.0 x 77.6 x 8.25 mm, 227 grams',
    waterResistance: 'IP68 rated, up to 6 metres for 30 minutes',
    security: 'Face ID enabled by TrueDepth camera',
    sim: 'Dual eSIM or Nano-SIM + eSIM',
    sensors:
      'LiDAR Scanner, barometer, high dynamic range gyro, high-g accelerometer, proximity, dual ambient light',
    audio: 'Stereo speakers, Spatial Audio, Dolby Atmos playback',
    warranty: '1 Year Manufacturer Local Warranty'
  },
  [COMPARE_SPEC_BRANDS.SAMSUNG]: {
    display:
      '6.8-inch Dynamic AMOLED 2X, 120Hz adaptive refresh, HDR10+, 2600 nits peak brightness',
    battery: '5,000 mAh intelligent all-day battery',
    camera:
      '200MP Wide (f/1.7), 50MP 5x Telephoto, 10MP 3x Telephoto, 12MP Ultra Wide, 8K video at 30fps',
    chip: 'Snapdragon 8 Gen 3 for Galaxy, Octa-Core, Adreno 750 GPU, 12GB RAM',
    os: 'Android 14 with One UI 6.1',
    connectivity:
      '5G (sub-6 GHz and mmWave), Wi-Fi 7 (802.11be), Bluetooth 5.3, Ultra Wideband (UWB), NFC',
    charging:
      '45W Super Fast Charging 2.0, 15W Fast Wireless Charging 2.0, Wireless PowerShare',
    dimensions: '162.3 x 79.0 x 8.6 mm, 232 grams',
    waterResistance: 'IP68 rated, up to 1.5 metres for 30 minutes',
    security: 'Ultrasonic fingerprint sensor, face recognition, Samsung Knox',
    sim: 'Nano-SIM + eSIM',
    sensors:
      'Accelerometer, barometer, gyro, geomagnetic, hall, proximity, light sensor',
    audio: 'Stereo speakers tuned by AKG, Dolby Atmos',
    warranty: '1 Year Manufacturer Local Warranty'
  },
  [COMPARE_SPEC_BRANDS.DEFAULT]: {
    display: '6.7-inch FHD+ AMOLED, 120Hz refresh rate',
    battery: '5,000 mAh all-day battery',
    camera:
      '50MP Main (f/1.8), 12MP Ultra Wide (f/2.2), 10MP Telephoto (3x optical zoom), 4K video recording',
    chip: 'Snapdragon 8 Gen 2 Mobile Platform, 8GB RAM',
    os: 'Android 14',
    connectivity: '5G, Wi-Fi 6E, Bluetooth 5.3, NFC',
    charging: '30W wired fast charging, 15W wireless charging',
    dimensions: '160.0 x 75.0 x 8.3 mm, 199 grams',
    waterResistance: 'IP68 rated, up to 1.5 metres for 30 minutes',
    security: 'In-display fingerprint sensor, face unlock',
    sim: 'Nano-SIM + eSIM',
    sensors: 'Accelerometer, gyro, proximity, ambient light, compass',
    audio: 'Stereo speakers, Hi-Res Audio',
    warranty: '1 Year Manufacturer Local Warranty'
  }
};
